import {CampaignTreeType} from 'common/src/modules/campaigns/campaigns.types';
import {deleteCard} from 'common/src/modules/campaigns/store/campaignLogic/actions/deleteCard';
import toConnectedMapNode from 'common/src/modules/campaigns/store/campaignLogic/utils/toConnectedMapNode';
import {DisconnectedMapNode} from 'common/src/modules/nodes/nodes.types';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {CampaignSetupAgentToolName} from '../../../campaignSetupAgent.enums';
import refreshCampaignIssues from '../../../utils/refreshCampaignIssues';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';

// Tool schema - accepts either card ID or card name to identify the card to delete
const deleteCardToolSchema = z
  .object({
    cardId: z.number().nullable().optional().describe('The ID of the card to delete'),
    cardName: z.string().nullable().optional().describe('The name of the card to delete'),
  })
  .refine((data) => data.cardId !== undefined || data.cardName !== undefined, {
    message: 'Either cardId or cardName must be provided',
    path: ['cardId', 'cardName'],
  });

function deleteCardTool(store: CLStore) {
  async function execute({
    input: {cardId, cardName},
  }: ToolExecuteParams<typeof deleteCardToolSchema>): Promise<ToolResult> {
    try {
      const state = store.getState();
      const nodes = state.campaignPage.campaignLogic.nodes[CampaignTreeType.Campaign];

      if (!nodes) {
        return toolError(
          `No nodes found for campaign tree type ${CampaignTreeType.Campaign}. Make sure you're in a campaign context.`,
        );
      }

      // Find the card to delete
      let nodeToDelete;
      let nodeIdToDelete;

      if (cardId !== undefined) {
        // Search by ID
        nodeToDelete = nodes[cardId];
        nodeIdToDelete = cardId;

        if (!nodeToDelete) {
          return toolError(
            `Card with ID ${cardId} not found. Available card IDs: ${Object.keys(nodes).join(
              ', ',
            )}`,
          );
        }
      } else if (cardName !== undefined) {
        // Search by name (case-insensitive)
        const matchingEntries = Object.entries(nodes).filter(
          ([_, node]) =>
            node.name?.toLowerCase().includes(cardName.toLowerCase()) ||
            node.description?.toLowerCase().includes(cardName.toLowerCase()),
        );

        if (matchingEntries.length === 0) {
          const availableNames = Object.values(nodes)
            .map((node) => node.name || node.description)
            .filter(Boolean)
            .join(', ');

          return toolError(
            `No card found with name containing "${cardName}". Available cards: ${availableNames}`,
          );
        }

        if (matchingEntries.length > 1) {
          const matchingNames = matchingEntries
            .map(([id, node]) => `ID: ${id}, Name: "${node.name || node.description}"`)
            .join('; ');

          return toolError(
            `Multiple cards found matching "${cardName}". Please be more specific or use card ID. Matches: ${matchingNames}`,
          );
        }

        // Exactly one match found
        [nodeIdToDelete, nodeToDelete] = matchingEntries[0];
        nodeIdToDelete = parseInt(nodeIdToDelete);
      }

      if (!nodeToDelete) {
        return toolError(
          `No card found with name containing "${cardName}". Available cards: ${Object.keys(
            nodes,
          ).join(', ')}`,
        );
      }

      // Convert disconnected node to connected node for the deleteCard thunk
      const connectedNodeToDelete = toConnectedMapNode(nodeToDelete as DisconnectedMapNode);

      // Dispatch the deleteCard thunk
      const result = await store.dispatch(
        deleteCard({
          nodeToDelete: connectedNodeToDelete,
          campaignTreeType: CampaignTreeType.Campaign,
        }),
      );

      if (deleteCard.fulfilled.match(result)) {
        // Refresh campaign issues after successful deletion
        await refreshCampaignIssues(store);

        const cardIdentifier =
          nodeToDelete.name || nodeToDelete.description || `ID ${nodeIdToDelete}`;

        return toolResult(
          `Successfully deleted card "${cardIdentifier}" from the campaign setup. Any connections to other cards have been automatically updated.`,
        );
      } else {
        const errorMessage = result.error?.message || 'Unknown error occurred';

        return toolError(`Failed to delete card: ${errorMessage}`);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

      return toolError(`Error deleting card: ${errorMessage}`);
    }
  }

  return tool({
    name: CampaignSetupAgentToolName.DeleteCard,
    displayName: 'Deleting Campaign Card',
    description:
      'Deletes a card from the campaign setup by card ID or name. Automatically handles updating connections to other cards when the card is removed.',
    execute,
    inputSchema: deleteCardToolSchema,
  });
}

export default deleteCardTool;
